import React from 'react';
import {
  FEED_CARD_SHELL,
  FEED_CHIP,
  FEED_INSPECT_BTN,
  FEED_TITLE,
  DD_SECTION_DESC,
  DD_SECTION_TITLE,
} from './investigation/feedCardStyles';
import MatchBadge from './investigation/MatchBadge';
import ResultKeywordTags from './investigation/ResultKeywordTags';
import ExposedPortsGroups from './ExposedPortsGroups';

/**
 * Native infrastructure cards (DNS / TLS / ASN) — Email Intelligence and Corporate Due Diligence.
 *
 * @param {{
 *   rows?: object[],
 *   loading?: boolean,
 *   onInspect?: Function,
 *   variant?: 'email' | 'corporate',
 * }} props
 */
export default function EmailInfrastructureSection({
  rows = [],
  loading = false,
  onInspect,
  variant = 'email',
}) {
  const isCorporate = variant === 'corporate';
  const sectionLabel = isCorporate ? 'Corporate infrastructure' : 'Email infrastructure';
  const sectionTitle = isCorporate ? 'Corporate Infrastructure' : 'Email Server & Routing';
  const sectionHint = isCorporate
    ? 'Native DNS, TLS certificate, ASN (Team Cymru), BGPView routing, and expanded port discovery'
    : 'Consolidated domain + MX routing, TLS, ASN (Team Cymru), and lightweight service discovery';
  const testId = isCorporate ? 'corp-infra' : 'email-infra';

  if (loading && (!rows || rows.length === 0)) {
    return (
      <section className="space-y-3" aria-label={sectionLabel} data-testid={`${testId}-loading`}>
        <article className={`${FEED_CARD_SHELL} animate-pulse border-cyan-500/20`} aria-busy="true">
          <div className="h-4 w-56 rounded bg-slate-800/80" />
          <div className="mt-3 h-16 rounded-xl bg-slate-900/80" />
        </article>
      </section>
    );
  }

  if (!rows || rows.length === 0) return null;

  return (
    <section className="space-y-3" aria-label={sectionLabel} data-testid={`${testId}-section`}>
      <div className="px-1">
        <h4 className={DD_SECTION_TITLE.cyan}>{sectionTitle}</h4>
        <p className={DD_SECTION_DESC}>{sectionHint}</p>
      </div>

      {rows.map((res, idx) => {
        const roleRaw = String(
          res.details?.corp_infra_role || res.details?.email_infra_role || 'domain',
        );
        const isRouting = !isCorporate && (roleRaw === 'routing' || res.details?.email_server_routing);
        const roleLabel = isCorporate
          ? roleRaw === 'mx_host'
            ? 'MX Host Infrastructure'
            : 'Domain Infrastructure'
          : isRouting
            ? 'Email Server & Routing'
            : roleRaw === 'mx_host'
              ? 'MX Host'
              : 'Domain';
        const emailDomain = String(
          res.details?.email_domain || res.details?.domain || res.details?.hostname || '',
        ).trim();
        const hostCount = typeof res.details?.host_count === 'number' ? res.details.host_count : null;
        const totalServices =
          typeof res.details?.total_services === 'number' ? res.details.total_services : null;
        const infra = res.details?.infra_inspect || {};
        const exposedPorts =
          infra.exposed_ports || res.details?.exposed_ports || res.exposed_ports || [];
        const routingHosts = Array.isArray(res.details?.routing_hosts)
          ? res.details.routing_hosts
          : [];
        const title = isRouting
          ? res.title ||
            (emailDomain ? `Email Server & Routing — ${emailDomain}` : 'Email Server & Routing')
          : res.title ||
            `${isCorporate ? 'Corporate' : 'Email'} infrastructure — ${
              res.details?.hostname || emailDomain
            }`;

        return (
          <div
            key={`${testId}-${res.details?.hostname || idx}-${idx}`}
            className={`${FEED_CARD_SHELL} border-cyan-500/30 hover:border-cyan-400/50`}
            data-testid={`${testId}-card`}
          >
            <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
              <div className="flex min-w-0 flex-1 items-start gap-4 sm:gap-5">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-cyan-500/30 bg-slate-900/80 text-cyan-400 shadow-inner transition-all">
                  <span className="text-2xl" aria-hidden>
                    🛰️
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-0.5 flex flex-wrap items-center justify-between gap-2">
                    <span className="flex flex-wrap items-center gap-1 text-[10px] font-black uppercase tracking-[0.15em] text-cyan-300">
                      {isRouting ? 'Email Server & Routing' : sectionTitle}
                    </span>
                  </div>
                  <div className="min-w-0 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className={FEED_TITLE}>{title}</h3>
                      <MatchBadge value={res.source_reliability ?? res.score ?? 78} />
                    </div>
                    <div
                      className="flex flex-wrap items-center gap-1"
                      data-testid={`${testId}-tech-badges`}
                    >
                      <span className={FEED_CHIP}>{roleLabel}</span>
                      {emailDomain ? (
                        <span className={FEED_CHIP}>
                          {isCorporate ? 'DOMAIN' : 'EMAIL DOMAIN'}: {emailDomain}
                        </span>
                      ) : null}
                      {hostCount !== null ? (
                        <span className={FEED_CHIP}>HOSTS: {hostCount}</span>
                      ) : null}
                      {totalServices !== null ? (
                        <span className={FEED_CHIP}>SERVICES: {totalServices}</span>
                      ) : null}
                      {infra.web_server ? (
                        <span className={FEED_CHIP}>WEB: {infra.web_server}</span>
                      ) : null}
                      {routingHosts.map((entry) => {
                        const host = typeof entry === 'string' ? entry : entry?.host;
                        const role = typeof entry === 'string' ? '' : entry?.role;
                        if (!host) return null;
                        return (
                          <span key={`${host}-${role}`} className={FEED_CHIP}>
                            {role ? `${role}: ${host}` : host}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <ResultKeywordTags res={res} />
                  {Array.isArray(exposedPorts) && exposedPorts.length > 0 ? (
                    <div className="mt-3">
                      <p className="mb-1.5 text-[9px] font-black uppercase tracking-widest text-slate-500">
                        Open ports
                      </p>
                      <ExposedPortsGroups ports={exposedPorts} />
                    </div>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onInspect?.(res)}
                className={FEED_INSPECT_BTN}
                data-testid={`${testId}-inspect-btn`}
              >
                Inspect
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
}
